import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { formResolver } from '@/lib/form-resolver';
import { z } from 'zod';
import { useI18n } from '@/i18n';
import { 
  Button, 
  Card, 
  Skeleton,
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  FormDescription,
} from '@/components/ui';
import { Input as BaseInput } from '@/components/ui/input';
import { Textarea as BaseTextarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Settings2, Sparkles, Database, FileText, CheckCircle2, AlertCircle, Plug } from 'lucide-react';
import { Collapsible } from '@vanguard-atlas/shared/src/components/ui';
import { cn } from '@/lib/utils';
import { 
  useCollection, 
  useCreateCollection, 
  useUpdateCollection,
} from '@/hooks/use-knowledge';
import {
  useKnowledgeConfig,
  type KnowledgeDefaultsConfig,
  type ChunkingStrategy,
} from '@/hooks/use-knowledge-config';
import { useEmbeddingModels, embeddingProviderLabel } from '@/hooks/use-embedding-models';
import { useProviders } from '@/features/datasources/hooks/use-providers';
import { useDataSources } from '@/features/datasources/hooks/use-datasources';
import apiClient from '@/lib/api-client';
import type { EnrichedProviderManifest } from '@/features/datasources/types';

// =============================================================================
// Form Schema
// =============================================================================

const collectionFormSchema = z.object({
  name: z.string()
    .min(1, 'Name is required')
    .max(100, 'Name must be less than 100 characters')
    .regex(/^[\w\s\-]+$/, 'Name can only contain letters, numbers, spaces, and hyphens'),
  description: z.string().max(1000, 'Description must be less than 1000 characters').optional(),
  mode: z.enum(['rag', 'full_document']).default('rag'),
});

type CollectionFormData = z.infer<typeof collectionFormSchema>;

// =============================================================================
// Strategy Options
// =============================================================================

const STRATEGY_OPTIONS: { value: ChunkingStrategy; label: string }[] = [
  { value: 'semantic_heuristic', label: 'Semantic (Heuristic)' },
  { value: 'semantic_llm', label: 'Semantic (LLM)' },
  { value: 'paragraph', label: 'Paragraph / Fixed Size' },
];

/**
 * Recommended enrichment settings per chunking strategy (ISS-380).
 *
 * Shared with the enrichment design (rag-pipeline-final-architecture §3.4):
 * - semantic_llm: chunking LLM-native → full enrichment (context + questions + taxonomy)
 * - semantic_heuristic: context + taxonomy; questions optional (deactivated by default)
 * - fixed/paragraph: deterministic chunking → no LLM chunk context; taxonomy only
 *
 * The user can override each toggle after the strategy change.
 */
const STRATEGY_ENRICHMENT_DEFAULTS: Record<
  ChunkingStrategy,
  { enrich_with_llm: boolean; extract_questions: boolean; auto_classify: boolean }
> = {
  semantic_llm: { enrich_with_llm: true, extract_questions: true, auto_classify: true },
  semantic_heuristic: { enrich_with_llm: true, extract_questions: false, auto_classify: true },
  paragraph: { enrich_with_llm: false, extract_questions: false, auto_classify: true },
};

// =============================================================================
// Loading Skeleton
// =============================================================================

function FormSkeleton() {
  return (
    <Card className="p-6 max-w-2xl">
      <div className="space-y-6">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-10 w-full" />
        <div className="grid grid-cols-2 gap-4">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      </div>
    </Card>
  );
}

// =============================================================================
// Collection Form Page
// =============================================================================

export function CollectionFormPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const kbId = searchParams.get('kb');
  const isEditing = Boolean(id);

  // Fetch existing collection if editing
  const { data: collection, isLoading: isLoadingCollection, isError } = useCollection(
    id || '',
    { enabled: !!id }
  );

  // Fetch tenant knowledge defaults (for create mode)
  const { data: tenantDefaults } = useKnowledgeConfig();

  // Mutations
  const createCollection = useCreateCollection();
  const updateCollection = useUpdateCollection();

  // Processing config state (create mode only)
  const [useDefaults, setUseDefaults] = useState(true);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [customConfig, setCustomConfig] = useState<KnowledgeDefaultsConfig | null>(null);
  // Repository to bind after collection creation (ISS-380).
  const [datasourceToBind, setDatasourceToBind] = useState('');

  // Initialize custom config from tenant defaults when they load
  useEffect(() => {
    if (tenantDefaults && !customConfig) {
      setCustomConfig(structuredClone(tenantDefaults));
    }
  }, [tenantDefaults, customConfig]);

  // Form setup
  const form = useForm<CollectionFormData>({
    resolver: formResolver(collectionFormSchema),
    defaultValues: {
      name: '',
      description: '',
      mode: 'rag',
    },
  });

  // Populate form when editing
  useEffect(() => {
    if (collection) {
      form.reset({
        name: collection.name,
        description: collection.description || '',
        mode: collection.mode || 'rag',
      });
    }
  }, [collection, form]);

  const onSubmit = async (data: CollectionFormData) => {
    try {
      if (isEditing && id) {
        await updateCollection.mutateAsync({
          id,
          name: data.name,
          description: data.description || undefined,
        });
        toast.success('Collection updated successfully');
      } else {
        // Build create payload
        const createPayload: Record<string, unknown> = {
          name: data.name,
          description: data.description || undefined,
          mode: data.mode || 'rag',
          ...(kbId ? { knowledge_base_id: kbId } : {}),
        };

        // Only send processing_config if using custom settings
        if (!useDefaults && customConfig) {
          createPayload.processing_config = {
            chunking: {
              strategy: customConfig.chunking.strategy,
              min_tokens: customConfig.chunking.min_tokens,
              max_tokens: customConfig.chunking.max_tokens,
              target_tokens: customConfig.chunking.target_tokens,
              overlap_tokens: customConfig.chunking.overlap_tokens,
            },
            enrichment: {
              enrich_with_llm: customConfig.enrichment.enrich_with_llm,
              extract_questions: customConfig.enrichment.extract_questions,
              auto_classify: customConfig.enrichment.auto_classify,
            },
            embedding: customConfig.embedding.model_id
              ? { model: customConfig.embedding.model_id }
              : undefined,
          };
        }
        // When useDefaults=true, we don't send processing_config
        // → backend applies tenant defaults automatically

        const created = await createCollection.mutateAsync(createPayload as unknown as Parameters<typeof createCollection.mutateAsync>[0]);
        // ISS-380: bind the selected repository (if any) after creation.
        if (datasourceToBind) {
          await apiClient.post(`/api/v1/datasources/${datasourceToBind}/bind`, {
            collection_id: created.id,
          });
        }
        toast.success('Collection created successfully');
      }
      navigate(kbId ? `/knowledge/bases/${kbId}` : '/knowledge/collections');
    } catch (error) {
      toast.error(isEditing ? 'Failed to update collection' : 'Failed to create collection');
      console.error('Form submission error:', error);
    }
  };

  const isPending = createCollection.isPending || updateCollection.isPending;

  // Watch mode to conditionally show processing config
  const selectedMode = form.watch('mode');

  // Loading state for edit mode
  if (isEditing && isLoadingCollection) {
    return (
      <div className="p-6">
        <div className="mb-6">
          <Skeleton className="h-8 w-48 mb-2" />
          <Skeleton className="h-5 w-64" />
        </div>
        <FormSkeleton />
      </div>
    );
  }

  // Error state
  if (isEditing && isError) {
    return (
      <div className="p-6">
        <Card className="p-8 text-center">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            Collection not found
          </h3>
          <p className="text-gray-500 dark:text-gray-400 mb-4">
            The collection you&apos;re trying to edit doesn&apos;t exist or you don&apos;t have access to it.
          </p>
          <Button variant="outline" onClick={() => navigate('/knowledge/collections')}>
            Back to Collections
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {isEditing ? 'Edit Collection' : 'New Collection'}
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            {isEditing 
              ? 'Update collection settings and configuration' 
              : 'Create a new knowledge collection for document storage'}
          </p>
        </div>
        <div className="flex gap-3">
          <Button 
            variant="outline" 
            onClick={() => navigate('/knowledge/collections')}
            disabled={isPending}
          >
            {t('common.cancel')}
          </Button>
          <Button 
            onClick={form.handleSubmit(onSubmit)}
            disabled={isPending}
          >
            {isPending ? 'Saving...' : t('common.save')}
          </Button>
        </div>
      </div>

      {/* Form */}
      <Card className="p-6 max-w-2xl">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {/* Name */}
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Collection Name *</FormLabel>
                  <FormControl>
                    <BaseInput
                      placeholder="e.g., Runbooks, Knowledge Base"
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    A unique name for this collection (max 100 characters)
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Description */}
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <BaseTextarea
                      placeholder="Describe the purpose and content of this collection..."
                      rows={3}
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    Optional description to help identify this collection
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Collection Mode (Create mode only) */}
            {!isEditing && (
              <FormField
                control={form.control}
                name="mode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('admin.knowledge.form.mode')}</FormLabel>
                    <FormControl>
                      <div className="grid grid-cols-2 gap-3">
                        <label
                          className={cn(
                            'flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition-colors',
                            field.value === 'rag'
                              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                              : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                          )}
                        >
                          <input
                            type="radio"
                            value="rag"
                            checked={field.value === 'rag'}
                            onChange={() => field.onChange('rag')}
                            className="mt-1"
                          />
                          <div>
                            <div className="flex items-center gap-2">
                              <Database className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                              <span className="font-medium text-gray-900 dark:text-gray-100">
                                {t('admin.knowledge.form.modeRag')}
                              </span>
                            </div>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              {t('admin.knowledge.form.modeRagDescription')}
                            </p>
                          </div>
                        </label>
                        <label
                          className={cn(
                            'flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition-colors',
                            field.value === 'full_document'
                              ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                              : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                          )}
                        >
                          <input
                            type="radio"
                            value="full_document"
                            checked={field.value === 'full_document'}
                            onChange={() => field.onChange('full_document')}
                            className="mt-1"
                          />
                          <div>
                            <div className="flex items-center gap-2">
                              <FileText className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                              <span className="font-medium text-gray-900 dark:text-gray-100">
                                {t('admin.knowledge.form.modeFullDocument')}
                              </span>
                            </div>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              {t('admin.knowledge.form.modeFullDocumentDescription')}
                            </p>
                          </div>
                        </label>
                      </div>
                    </FormControl>
                    <FormDescription>
                      {t('admin.knowledge.form.modeHelp')}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {/* Processing Configuration (Create mode only, RAG mode only) */}
            {!isEditing && selectedMode === 'rag' && (
              <div className="border rounded-lg border-gray-200 dark:border-gray-700">
                <Collapsible
                  className="p-4"
                  testId="collection-processing-config"
                  open={showAdvanced}
                  onOpenChange={setShowAdvanced}
                  trigger={
                    <span className="flex items-center gap-2">
                      <Settings2 className="h-4 w-4 text-gray-500" />
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        Processing Configuration
                      </span>
                      {useDefaults && (
                        <span className="text-xs bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 px-2 py-0.5 rounded-full">
                          Using workspace defaults
                        </span>
                      )}
                    </span>
                  }
                >
                  <div className="mt-4 space-y-4 border-t border-gray-200 dark:border-gray-700 pt-4">
                    {/* Default vs Custom Toggle */}
                    <div className="flex items-center gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="configMode"
                          checked={useDefaults}
                          onChange={() => setUseDefaults(true)}
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                          Use workspace defaults
                        </span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="configMode"
                          checked={!useDefaults}
                          onChange={() => setUseDefaults(false)}
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                          Custom configuration
                        </span>
                      </label>
                    </div>

                    {/* Defaults Preview or Custom Form */}
                    {useDefaults ? (
                      <DefaultsPreview defaults={tenantDefaults} />
                    ) : (
                      customConfig && (
                        <CustomConfigForm
                          config={customConfig}
                          onChange={setCustomConfig}
                        />
                      )
                    )}
                  </div>
                </Collapsible>
              </div>
            )}

            {/* Repository linking (ISS-380) — always visible in create mode */}
            {!isEditing && (
              <div className="border rounded-lg border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-2 p-4">
                  <Plug className="h-4 w-4 text-gray-500" />
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {t('admin.knowledge.repository.title')}
                  </span>
                </div>
                <div className="px-4 pb-4">
                  <RepositoryLinkSection
                    selectedId={datasourceToBind}
                    onSelectChange={setDatasourceToBind}
                  />
                </div>
              </div>
            )}

            {/* Editing mode: Show read-only processing info */}
            {isEditing && (
              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  Processing configuration can be changed from the collection detail page.
                </p>
              </div>
            )}
          </form>
        </Form>
      </Card>
    </div>
  );
}

// =============================================================================
// Defaults Preview (readonly)
// =============================================================================

function DefaultsPreview({ defaults }: { defaults?: KnowledgeDefaultsConfig }) {
  if (!defaults) {
    return (
      <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded text-sm text-gray-500">
        Loading workspace defaults...
      </div>
    );
  }

  const strategyLabel = STRATEGY_OPTIONS.find(o => o.value === defaults.chunking.strategy)?.label
    || defaults.chunking.strategy;

  return (
    <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded space-y-2 text-sm">
      <div className="grid grid-cols-2 gap-x-4 gap-y-1">
        <span className="text-gray-500 dark:text-gray-400">Strategy:</span>
        <span className="text-gray-900 dark:text-gray-100">{strategyLabel}</span>
        <span className="text-gray-500 dark:text-gray-400">Target tokens:</span>
        <span className="text-gray-900 dark:text-gray-100">{defaults.chunking.target_tokens}</span>
        <span className="text-gray-500 dark:text-gray-400">Overlap:</span>
        <span className="text-gray-900 dark:text-gray-100">{defaults.chunking.overlap_tokens}</span>
        <span className="text-gray-500 dark:text-gray-400">LLM enrichment:</span>
        <span className="text-gray-900 dark:text-gray-100">{defaults.enrichment.enrich_with_llm ? 'Yes' : 'No'}</span>
        <span className="text-gray-500 dark:text-gray-400">Auto-classify:</span>
        <span className="text-gray-900 dark:text-gray-100">{defaults.enrichment.auto_classify ? 'Yes' : 'No'}</span>
        <span className="text-gray-500 dark:text-gray-400">Embedding model:</span>
        <span className="text-gray-900 dark:text-gray-100">{defaults.embedding.model_id || 'Platform default'}</span>
      </div>
      <p className="text-xs text-gray-400 mt-2">
        Change defaults in Settings → Knowledge.
      </p>
    </div>
  );
}

// =============================================================================
// Custom Config Form
// =============================================================================

function CustomConfigForm({
  config,
  onChange,
}: {
  config: KnowledgeDefaultsConfig;
  onChange: (c: KnowledgeDefaultsConfig) => void;
}) {
  const { t } = useI18n();
  return (
    <div className="space-y-4">
      {/* Chunking */}
      <div>
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Chunking</h4>
        <div className="space-y-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Strategy</label>
            <select
              value={config.chunking.strategy}
              onChange={(e) =>
                onChange({
                  ...config,
                  chunking: { ...config.chunking, strategy: e.target.value as ChunkingStrategy },
                  // ISS-380: enrichment adapts to the selected strategy
                  // (recommended defaults, overridable afterwards).
                  enrichment: { ...STRATEGY_ENRICHMENT_DEFAULTS[e.target.value as ChunkingStrategy] },
                })
              }
              className="w-full px-3 py-2 text-sm border rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            >
              {STRATEGY_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <MiniNumberField
              label="Target Tokens"
              value={config.chunking.target_tokens}
              min={100}
              max={1500}
              onChange={(v) =>
                onChange({
                  ...config,
                  chunking: { ...config.chunking, target_tokens: v },
                })
              }
            />
            <MiniNumberField
              label="Max Tokens"
              value={config.chunking.max_tokens}
              min={100}
              max={2000}
              onChange={(v) =>
                onChange({
                  ...config,
                  chunking: { ...config.chunking, max_tokens: v },
                })
              }
            />
            <MiniNumberField
              label="Min Tokens"
              value={config.chunking.min_tokens}
              min={50}
              max={500}
              onChange={(v) =>
                onChange({
                  ...config,
                  chunking: { ...config.chunking, min_tokens: v },
                })
              }
            />
            <MiniNumberField
              label="Overlap"
              value={config.chunking.overlap_tokens}
              min={0}
              max={200}
              onChange={(v) =>
                onChange({
                  ...config,
                  chunking: { ...config.chunking, overlap_tokens: v },
                })
              }
            />
          </div>
        </div>
      </div>

      {/* Enrichment */}
      <div>
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-1">
          <Sparkles className="h-3.5 w-3.5" /> Enrichment
        </h4>
        <p className="text-xs text-gray-400 mb-2">
          {t('admin.knowledge.repository.enrichmentHint')}{' '}
          <span className="font-medium">
            {STRATEGY_OPTIONS.find(o => o.value === config.chunking.strategy)?.label ?? config.chunking.strategy}
          </span>{' '}
          {t('admin.knowledge.repository.enrichmentOverride')}
        </p>
        <div className="space-y-2">
          <MiniToggle
            label="Enrich with LLM"
            checked={config.enrichment.enrich_with_llm}
            onChange={(v) =>
              onChange({
                ...config,
                enrichment: { ...config.enrichment, enrich_with_llm: v },
              })
            }
          />
          <MiniToggle
            label="Extract questions"
            checked={config.enrichment.extract_questions}
            onChange={(v) =>
              onChange({
                ...config,
                enrichment: { ...config.enrichment, extract_questions: v },
              })
            }
          />
          <MiniToggle
            label="Auto-classify"
            checked={config.enrichment.auto_classify}
            onChange={(v) =>
              onChange({
                ...config,
                enrichment: { ...config.enrichment, auto_classify: v },
              })
            }
          />
        </div>
      </div>

      {/* Embedding Model */}
      <div>
        <label className="block text-xs text-gray-500 mb-1">Embedding Model</label>
        <EmbeddingModelSelect
          value={config.embedding.model_id ?? ''}
          onChange={(modelId) =>
            onChange({
              ...config,
              embedding: { ...config.embedding, model_id: modelId || null },
            })
          }
        />
      </div>
    </div>
  );
}

// =============================================================================
// Mini Sub-Components
// =============================================================================

function MiniNumberField({
  label,
  value,
  min,
  max,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  onChange: (v: number) => void;
}) {
  return (
    <div>
      <label className="block text-xs text-gray-500 mb-1">{label}</label>
      <input
        type="number"
        value={value}
        min={min}
        max={max}
        onChange={(e) => {
          const v = parseInt(e.target.value, 10);
          if (!isNaN(v) && v >= min && v <= max) onChange(v);
        }}
        className="w-full px-2 py-1.5 text-sm border rounded border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
      />
    </div>
  );
}

function MiniToggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-2 cursor-pointer">
      <div
        className={cn(
          'relative w-8 h-4.5 rounded-full cursor-pointer transition-colors',
          checked ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
        )}
        onClick={() => onChange(!checked)}
      >
        <div
          className={cn(
            'absolute top-0.5 left-0.5 w-3.5 h-3.5 bg-white rounded-full transition-transform',
            checked && 'translate-x-3.5'
          )}
        />
      </div>
      <span className="text-sm text-gray-700 dark:text-gray-300">{label}</span>
    </label>
  );
}

// =============================================================================
// Embedding Model Select
// =============================================================================

/**
 * Loads the tenant's active embedding models (ISS-380): catalog from the
 * admin embedding-models endpoint (active providers + tenant policy).
 * No free-text model IDs.
 */
function EmbeddingModelSelect({
  value,
  onChange,
}: {
  value: string;
  onChange: (modelId: string) => void;
}) {
  const { t } = useI18n();
  const { data: models, isLoading } = useEmbeddingModels();

  return (
    <div>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 text-sm border rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
        disabled={isLoading}
      >
        <option value="">{t('admin.knowledge.repository.embeddingDefault')}</option>
        {models?.map((m) => (
          <option key={m.model_id} value={m.model_id}>
            {m.model_id} ({embeddingProviderLabel(m.provider_type)}
            {m.dimensions ? ` · ${m.dimensions}d` : ''}
            {m.is_default ? ' · default' : ''})
          </option>
        ))}
      </select>
      {!isLoading && models?.length === 0 && (
        <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
          {t('admin.knowledge.repository.embeddingEmpty')}
        </p>
      )}
    </div>
  );
}

// =============================================================================
// Repository Linking (ISS-380)
// =============================================================================

/**
 * Always-visible connector section for collection creation: lists provider
 * manifests with their enrollment status (ready / needs_setup / needs_install)
 * and, when ready, allows binding an existing datasource of that provider.
 */
function RepositoryLinkSection({
  selectedId,
  onSelectChange,
}: {
  selectedId: string;
  onSelectChange: (datasourceId: string) => void;
}) {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { data: providers, isLoading: loadingProviders } = useProviders();
  const { data: datasources, isLoading: loadingDatasources } = useDataSources();

  const sourceOptions = (providerType: string) =>
    (datasources?.data ?? []).filter(
      (d) => d.provider_type === providerType && (d.status === 'active' || d.status === 'syncing')
    );

  if (loadingProviders || loadingDatasources) {
    return (
      <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded text-sm text-gray-500">
        {t('admin.knowledge.repository.loading')}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {(providers ?? []).map((p: EnrichedProviderManifest) => {
        const sourceList = sourceOptions(p.provider_type);
        const needsInstallation = p.enrollment_status !== 'ready';
        return (
          <div
            key={p.provider_type}
            className="flex items-center justify-between gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700"
          >
            <div className="flex items-center gap-2 min-w-0">
              <Plug className="h-4 w-4 text-gray-400 shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                  {p.display_name}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                  {!needsInstallation ? (
                    <>
                      <CheckCircle2 className="h-3 w-3 text-emerald-500" />
                      {t('admin.knowledge.repository.connected')}
                    </>
                  ) : (
                    <>
                      <AlertCircle className="h-3 w-3 text-amber-500" />
                      {t('admin.knowledge.repository.needsInstallation')}
                    </>
                  )}
                </p>
              </div>
            </div>

            {!needsInstallation && sourceList.length > 0 ? (
              <select
                className="max-w-[240px] px-2 py-1.5 text-sm border rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                value={selectedId}
                onChange={(e) => onSelectChange(e.target.value)}
              >
                <option value="">{t('admin.knowledge.repository.linkPlaceholder')}</option>
                {sourceList.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/knowledge/datasources')}
              >
                {t('admin.knowledge.repository.configure')}
              </Button>
            )}
          </div>
        );
      })}
      <p className="text-xs text-gray-400">
        {t('admin.knowledge.repository.hint')}
      </p>
    </div>
  );
}
